<template>
  <div>
    <rest-page :fetch-endpoint="fetchEndpoint" ref="restPage">
      <rest-form
        :save-endpoint="saveRulesEndpoint"
        :after-save="afterSaveRules"
        :model="model"
        :rules="{}"
        :readonly="readonly"
        ref="rulesForm"
      >
        <template v-if="model">
          <form-section
            :title="$t('ProjectSettings_EngagementRules_Title')"
            :description="$t('ProjectSettings_EngagementRules_Description')"
          >
            <el-row>
              <el-col :span="18">
                <toggle-list :data="rulesList" :model="model" />
              </el-col>
            </el-row>
            <template slot="controls">
              <save-button :click="saveRules" />
            </template>
          </form-section>
        </template>
      </rest-form>

      <rest-form
        :save-endpoint="saveTrackingEndpoint"
        :after-save="afterSaveTracking"
        :model="model"
        :rules="{}"
        :readonly="readonly"
        ref="trackingForm"
      >
        <template v-if="model">
          <form-section
            :title="$t('ProjectSettings_EngagementTracking_Title')"
            :description="$t('ProjectSettings_EngagementTracking_Description')"
          >
            <el-row>
              <el-col :span="18">
                <toggle-list :data="trackingList" :model="model" />
              </el-col>
            </el-row>
            <template slot="controls">
              <save-button :click="saveTracking" />
            </template>
          </form-section>
        </template>
      </rest-form>

      <form-section
        :title="$t('ProjectSettings_EngagementConditions_Title')"
        :description="$t('ProjectSettings_EngagementConditions_Description')"
      >
        <toggle
          v-model="engagementConditionsEnabled"
          :text="$t('ProjectSettings_EngagementConditions_Enable')"
        />

        <conditions-editor
          class="m-top-20"
          ref="conditions-editor"
          v-if="engagementConditionsEnabled"
          v-model="engagementConditions"
          :menu="engagementConditionsMenu"
          data-model-name="Segment"
          data-model-attribute="conditions"
          attribute-list-data-url="/segment/segment/attribute-list-data"
          :single-level="true"
        />

        <template slot="controls">
          <save-button :click="saveEngagementConditions" />
        </template>
      </form-section>
    </rest-page>
  </div>
</template>

<script>
import { projectEndpoint } from '@/core/helpers'
import FormSection from '@/common/components/form/FormSection'
import RestPage from '@/common/components/rest/RestPage'
import RestForm from '@/common/components/rest/RestForm'
import ToggleList from '../../../common/components/form/ToggleList'
import SaveButton from '@/common/components/form/SaveButton'
import RestApi from '@/core/services/RestApi'
import Toggle from '@/common/components/form/Toggle'
import ConditionsEditor from '@/common/components/ConditionsEditor'

export default {
  name: 'EngagementRulesTab',
  components: {
    ConditionsEditor,
    Toggle,
    SaveButton,
    ToggleList,
    FormSection,
    RestPage,
    RestForm
  },

  data() {
    return {
      model: {},
      engagementConditions: null,
      engagementConditionsEnabled: null
    }
  },

  computed: {
    fetchEndpoint() {
      if (this.$route.query.project_id) {
        return projectEndpoint({
          method: 'get',
          url: ``,
          params: {
            expand:
              'engagement_conditions, disable_email_open_tracking, disable_email_click_tracking'
          },
          handler: data => {
            this.model = {
              broadcast_disabled: data.broadcast_disabled,
              target_application_enabled: data.target_application_enabled,
              target_devices_enabled: data.target_devices_enabled,
              categories_required: data.categories_required,
              calculate_recipients: data.calculate_recipients,
              feature_seed_list: data.feature_seed_list,
              seed_list_enabled: data.seed_list_enabled,
              disable_email_open_tracking: data.disable_email_open_tracking,
              disable_email_click_tracking: data.disable_email_click_tracking
            }

            if (data.engagement_conditions) {
              this.engagementConditionsEnabled = 1
              this.engagementConditions = data.engagement_conditions
            } else {
              this.engagementConditionsEnabled = 0
              this.engagementConditions = {
                operator: 'AND',
                '0': {
                  operator: 'AND',
                  '0': {}
                }
              }
            }
          }
        })
      } else {
        return null
      }
    },

    saveRulesEndpoint() {
      return projectEndpoint({
        method: 'patch',
        url: ``,
        params: {
          data: this.model
        }
      })
    },

    saveTrackingEndpoint() {
      return projectEndpoint({
        method: 'patch',
        url: ``,
        params: {
          data: this.model
        }
      })
    },

    rulesList() {
      let rules = [
        {
          code: 'broadcast_disabled',
          name: this.$t('ProjectSettings_EngagementRules_BroadcastDisabled'),
          description: this.$t(
            'ProjectSettings_EngagementRules_BroadcastDisabled_Description'
          )
        },
        {
          code: 'calculate_recipients',
          name: this.$t('ProjectSettings_EngagementRules_CalculateRecipient'),
          description: this.$t(
            'ProjectSettings_EngagementRules_CalculateRecipient_Description'
          )
        },
        {
          code: 'categories_required',
          name: this.$t('ProjectSettings_EngagementRules_CategoriesRequired'),
          description: this.$t(
            'ProjectSettings_EngagementRules_CategoriesRequired_Description'
          )
        },
        {
          code: 'target_application_enabled',
          name: this.$t(
            'ProjectSettings_EngagementRules_TargetApplicationEnabled'
          ),
          description: this.$t(
            'ProjectSettings_EngagementRules_TargetApplicationEnabled_Description'
          )
        },
        {
          code: 'target_devices_enabled',
          name: this.$t('ProjectSettings_EngagementRules_TargetDevicesEnabled'),
          description: this.$t(
            'ProjectSettings_EngagementRules_TargetDevicesEnabled_Description'
          )
        }
      ]

      if (this.model.feature_seed_list) {
        rules.push({
          code: 'seed_list_enabled',
          name: this.$t('ProjectSettings_EngagementRules_SeedListEnabled'),
          description: this.$t(
            'ProjectSettings_EngagementRules_SeedListEnabled_Description'
          )
        })
      }

      return rules
    },

    trackingList() {
      return [
        {
          code: 'disable_email_open_tracking',
          name: this.$t('ProjectSettings_EngagementTracking_EmailOpenTracking'),
          description: this.$t(
            'ProjectSettings_EngagementTracking_EmailOpenTracking_Description'
          )
        },
        {
          code: 'disable_email_click_tracking',
          name: this.$t(
            'ProjectSettings_EngagementTracking_EmailClickTracking'
          ),
          description: this.$t(
            'ProjectSettings_EngagementTracking_EmailClickTracking_Description'
          )
        }
      ]
    },

    engagementConditionsMenu() {
      return {
        'main-choices': {
          tags_attribute: {
            title: 'Attribute',
            icon: 'attributes',
            has_submenu: false
          }
        }
      }
    },

    readonly() {
      return !this.$can('manageProjectTechnical')
    }
  },

  methods: {
    afterSaveRules(resp, loading) {
      this.$store.dispatch('reloadProjectConfig').then(loading())
    },

    afterSaveTracking(resp, loading) {
      this.$store.dispatch('reloadProjectConfig').then(loading())
    },

    switcher() {},

    saveRules() {
      this.$refs['rulesForm'].save()
    },

    saveTracking() {
      this.$refs['trackingForm'].save()
    },

    saveEngagementConditions() {
      if (this.engagementConditionsEnabled) {
        let conditions = this.$refs['conditions-editor'].serialize()
        this.updateEngagementConditions(conditions)
      } else {
        this.updateEngagementConditions(null)
      }
    },

    updateEngagementConditions(conditions) {
      this.$refs.restPage.loading = true
      RestApi(
        projectEndpoint({
          method: 'patch',
          url: ``,
          data: { engagement_conditions: conditions }
        })
      )
        .then(() => {
          this.$refs.restPage.loading = false
          this.$message({
            message: this.$t(
              'ProjectSettings_EngagementConditions_SuccessfullySaved'
            ),
            type: 'success'
          })
        })
        .catch(error => {
          this.$refs.restPage.loading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/settings/components/EngagementRulesTab.vue